import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = ({ userData }) => {
  const defaultAccessCard = userData?.accesscards?.find(
    ac => ac.accesscard_id === userData.default_accesscard_id
  );
  const schoolName = defaultAccessCard?.school?.school_name || 'Acuity CTE';
  const logoUrl = defaultAccessCard?.school?.logo_url || "https://acuitycte-files.s3.us-west-2.amazonaws.com/public/acuity-tech-logo.png";

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <Link to="/sms-notifications" style={styles.backButton}>
          ← Back to SMS Notifications
        </Link>
        <div style={styles.logoSection}>
          <img src={logoUrl} alt="Logo" style={styles.logo} />
          <h1 style={styles.schoolName}>{schoolName}</h1>
        </div>
      </div>

      <div style={styles.content}>
        <h1 style={styles.title}>Privacy Policy</h1>
        <p style={styles.lastUpdated}>Last Updated: {new Date().toLocaleDateString()}</p>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>1. Introduction</h2>
          <p style={styles.text}>
            {schoolName} ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy 
            explains how we collect, use, disclose, and safeguard your information when you use our SMS 
            notification service. By using our service, you consent to the data practices described in this policy.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>2. Information We Collect</h2>
          <p style={styles.text}>
            When you opt into our SMS notification service, we collect the following information:
          </p>
          <ul style={styles.list}>
            <li><strong>Personal Information:</strong> First name, last name, and email address</li>
            <li><strong>Contact Information:</strong> Mobile phone number for SMS delivery</li>
            <li><strong>Preferences:</strong> Your notification preferences and consent settings</li>
            <li><strong>Usage Data:</strong> Information about how you interact with our SMS service</li>
          </ul>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>3. How We Use Your Information</h2>
          <p style={styles.text}>
            We use the information we collect to:
          </p>
          <ul style={styles.list}>
            <li>Send SMS notifications based on your preferences</li>
            <li>Provide customer support and respond to inquiries</li>
            <li>Improve our services and user experience</li>
            <li>Comply with legal obligations and regulations</li>
            <li>Send important updates about our services</li>
          </ul>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>4. SMS Service Provider</h2>
          <p style={styles.text}>
            We use Twilio, a third-party SMS service provider, to deliver text messages. When you receive 
            SMS messages from us:
          </p>
          <ul style={styles.list}>
            <li>Your phone number is shared with Twilio for message delivery</li>
            <li>Twilio may collect delivery status and analytics data</li>
            <li>Twilio's privacy practices are governed by their own privacy policy</li>
            <li>We do not sell or rent your phone number to third parties</li>
          </ul>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>5. Data Storage and Security</h2>
          <p style={styles.text}>
            We implement appropriate security measures to protect your information:
          </p>
          <ul style={styles.list}>
            <li>All data is stored in secure, encrypted databases</li>
            <li>Access to your information is restricted to authorized personnel</li>
            <li>We use industry-standard security protocols and encryption</li>
            <li>Regular security audits and updates are performed</li>
          </ul>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>6. Data Retention</h2>
          <p style={styles.text}>
            We retain your information for as long as necessary to:
          </p>
          <ul style={styles.list}>
            <li>Provide SMS notification services</li>
            <li>Comply with legal obligations</li>
            <li>Resolve disputes and enforce agreements</li>
            <li>Improve our services</li>
          </ul>
          <p style={styles.text}>
            You may request deletion of your data at any time by contacting us.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>7. Your Rights and Choices</h2>
          <p style={styles.text}>
            You have the following rights regarding your personal information:
          </p>
          <ul style={styles.list}>
            <li><strong>Access:</strong> Request a copy of your personal information</li>
            <li><strong>Correction:</strong> Update or correct inaccurate information</li>
            <li><strong>Deletion:</strong> Request deletion of your information</li>
            <li><strong>Opt-out:</strong> Stop receiving SMS messages at any time</li>
            <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
          </ul>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>8. Opt-Out and Unsubscribe</h2>
          <p style={styles.text}>
            You can opt out of SMS notifications in several ways:
          </p>
          <ul style={styles.list}>
            <li>Reply "STOP" to any SMS message you receive</li>
            <li>Contact us directly to request removal</li>
            <li>Update your preferences through your account settings</li>
            <li>Email us at privacy@acuitycte.com</li>
          </ul>
          <p style={styles.text}>
            After opting out, we will stop sending SMS messages within 24 hours.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>9. Information Sharing</h2>
          <p style={styles.text}>
            We do not sell, trade, or otherwise transfer your personal information to third parties, except:
          </p>
          <ul style={styles.list}>
            <li>With your explicit consent</li>
            <li>To comply with legal requirements or court orders</li>
            <li>To protect our rights, property, or safety</li>
            <li>With service providers who assist in our operations (like Twilio)</li>
            <li>In connection with a business transfer or merger</li>
          </ul>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>10. Children's Privacy</h2>
          <p style={styles.text}>
            Our SMS notification service is not intended for children under 13 years of age. We do not 
            knowingly collect personal information from children under 13. If you are a parent or guardian 
            and believe your child has provided us with personal information, please contact us immediately.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>11. International Data Transfers</h2>
          <p style={styles.text}>
            Your information may be transferred to and processed in countries other than your own. We ensure 
            that such transfers comply with applicable data protection laws and that your information receives 
            adequate protection.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>12. Changes to This Policy</h2>
          <p style={styles.text}>
            We may update this Privacy Policy from time to time. We will notify you of any material changes by:
          </p>
          <ul style={styles.list}>
            <li>Posting the updated policy on our website</li>
            <li>Sending you an email notification</li>
            <li>Including a notice in our SMS messages</li>
          </ul>
          <p style={styles.text}>
            Your continued use of our SMS service after changes become effective constitutes acceptance of the updated policy.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>13. Contact Us</h2>
          <p style={styles.text}>
            If you have questions about this Privacy Policy or our data practices, please contact us:
          </p>
          <div style={styles.contactInfo}>
            <p><strong>Privacy Officer:</strong> privacy@acuitycte.com</p>
            <p><strong>General Support:</strong> support@acuitycte.com</p>
            <p><strong>Contact Name:</strong> Cristina Garcia</p>
            <p><strong>Phone:</strong> (888) 551-8653 Ext. 102</p>
            <p><strong>Address:</strong> 414 Yale Avenue Suite I, Claremont, CA 91711</p>
          </div>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>14. Legal Basis for Processing (GDPR)</h2>
          <p style={styles.text}>
            If you are located in the European Union, our legal basis for processing your personal information includes:
          </p>
          <ul style={styles.list}>
            <li><strong>Consent:</strong> You have given clear consent for us to process your data for SMS notifications</li>
            <li><strong>Legitimate Interest:</strong> To provide and improve our educational services</li>
            <li><strong>Contract:</strong> To fulfill our obligations under our service agreement</li>
            <li><strong>Legal Obligation:</strong> To comply with applicable laws and regulations</li>
          </ul>
        </div>

        <div style={styles.footer}>
          <p style={styles.footerText}>
            By using our SMS notification service, you acknowledge that you have read and understood this 
            Privacy Policy and consent to the collection and use of your information as described herein.
          </p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f8f9fa',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  header: {
    backgroundColor: 'white',
    padding: '20px 40px',
    boxShadow: '0 2px 10px rgba(29, 78, 137, 0.1)',
    borderBottom: '1px solid rgba(29, 78, 137, 0.1)'
  },
  backButton: {
    color: '#1D4E89',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500',
    display: 'inline-block',
    marginBottom: '20px',
    transition: 'color 0.3s ease'
  },
  logoSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px'
  },
  logo: {
    height: '40px',
    width: 'auto'
  },
  schoolName: {
    color: '#1D4E89',
    fontSize: '24px',
    fontWeight: '600',
    margin: 0
  },
  content: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '40px 20px'
  },
  title: {
    color: '#1D4E89',
    fontSize: '36px',
    fontWeight: '700',
    marginBottom: '10px',
    textAlign: 'center'
  },
  lastUpdated: {
    color: '#666',
    fontSize: '14px',
    textAlign: 'center',
    marginBottom: '40px',
    fontStyle: 'italic'
  },
  section: {
    marginBottom: '30px',
    backgroundColor: 'white',
    padding: '25px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(29, 78, 137, 0.05)',
    border: '1px solid rgba(29, 78, 137, 0.05)'
  },
  sectionTitle: {
    color: '#1D4E89',
    fontSize: '20px',
    fontWeight: '600',
    marginBottom: '15px',
    borderBottom: '2px solid rgba(29, 78, 137, 0.1)',
    paddingBottom: '8px'
  },
  text: {
    color: '#333',
    fontSize: '16px',
    lineHeight: '1.6',
    marginBottom: '15px'
  },
  list: {
    color: '#333',
    fontSize: '16px',
    lineHeight: '1.6',
    marginLeft: '20px',
    marginBottom: '15px'
  },
  contactInfo: {
    backgroundColor: 'rgba(29, 78, 137, 0.05)',
    padding: '20px',
    borderRadius: '8px',
    border: '1px solid rgba(29, 78, 137, 0.1)'
  },
  footer: {
    textAlign: 'center',
    marginTop: '40px',
    padding: '20px',
    backgroundColor: 'rgba(29, 78, 137, 0.05)',
    borderRadius: '12px',
    border: '1px solid rgba(29, 78, 137, 0.1)'
  },
  footerText: {
    color: '#1D4E89',
    fontSize: '16px',
    fontWeight: '500',
    margin: 0
  }
};

export default PrivacyPolicy; 